import { useState, useEffect, useRef } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { format, parseISO, isValid, startOfMonth, differenceInDays } from 'date-fns';
import { useCruises, Cruise } from '../context/CruiseContext';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { useItinerary } from '../context/ItineraryContext';
import { useAuth } from '../context/AuthContext';
import { isItineraryFeatureAvailable } from '../utils/offlineUtils';
import { ITINERARY_FILTER_DAYS } from '../config';
import { PlusCircle, X, ChevronLeft, Loader2, Plane, Train, Car, FileText, Dice1 as Dice, ChefHat, Info, AlertTriangle, Wifi, GlassWater, Calendar } from 'lucide-react';
import { CurrencyIcon } from '../components/CurrencyIcon';
import { v4 as uuidv4 } from 'uuid';
import { motion } from 'framer-motion';
import CruiseDatePicker from '../components/CruiseDatePicker';
import CruiseConflictModal from '../components/CruiseConflictModal';
import { checkCruiseConflict } from '../utils/dateUtils';
import toast from 'react-hot-toast';
import CustomCruiseIcon from '../components/CustomCruiseIcon';
import { debug } from '../utils/debug';

interface TravelDetail {
  id: string;
  type: 'rental-car' | 'train' | 'airplane';
  company?: string;
  reservationNumber?: string;
  departureTime?: string;
  arrivalTime?: string;
  departureLocation?: string;
  arrivalLocation?: string;
  flightNumber?: string;
  trainNumber?: string;
  carType?: string;
  pickupLocation?: string;
  returnLocation?: string;
  pickupTime?: string;
  returnTime?: string;
}

interface Port {
  id: string;
  name: string;
  date: string;
  arrivalTime?: string;
  departureTime?: string;
  excursion?: string;
  fromItinerary?: boolean; // Flag to mark ports loaded from itinerary
}

interface CruiseFormData {
  cruiseLine: string;
  shipName: string;
  departureDate: string;
  returnDate: string;
  departurePort: string;
  isGuaranteedCabin: boolean;
  cabinNumber?: string | null;
  cabinType: string;
  bookingNumber?: string | null;
  cruiseCost?: number;
  portTaxesFees?: number;
  additionalCosts?: number;
  isComplimentaryCasinoSailing: boolean;
  notes?: string;
  travelAgentName?: string;
  travelAgentPhone?: string;
  travelAgentEmail?: string;
  ports: Port[];
  travelDetails: TravelDetail[];
  diningTime?: string;
  hasDrinkPackage?: boolean;
  hasInternetPackage?: boolean;
  // New post-cruise fields
  gamblingWins?: number;
  gamblingLosses?: number;
  onboardExpenses?: number;
}

const AddCruise = () => {
  const { addCruise, cruises } = useCruises();
  const { cruiseLines, getShipsForCruiseLine } = useCruiseMetadata();
  const { getItinerarySummaryForShip, getItineraryByCompositeKey } = useItinerary();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [availableShips, setAvailableShips] = useState<{ active: string[]; retired: string[]; }>({ active: [], retired: [] });
  const [showPortsForm, setShowPortsForm] = useState(false);
  const [showTravelForm, setShowTravelForm] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [pendingCruiseData, setPendingCruiseData] = useState<CruiseFormData | null>(null);
  
  // Itinerary-related state
  const [availableDepartureDates, setAvailableDepartureDates] = useState<string[]>([]);
  const [itineraryDetails, setItineraryDetails] = useState<{ [sailingDate: string]: { cruiseLength: number; itineraryTitle: string } }>({});
  const [loadingDepartureDates, setLoadingDepartureDates] = useState(false);
  const [loadingItinerary, setLoadingItinerary] = useState(false);
  const [useItineraryMode, setUseItineraryMode] = useState(false);
  const [itinerarySelected, setItinerarySelected] = useState(false); // Track if an itinerary has been selected
  const [selectedItineraryDate, setSelectedItineraryDate] = useState('');
  const [selectedItineraryKey, setSelectedItineraryKey] = useState<string | null>(null); // Composite key for sync
  const [selectedItineraryTitle, setSelectedItineraryTitle] = useState<string | null>(null); // Title for countdown & display
  
  // Date conflict modal state
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictingCruise, setConflictingCruise] = useState<Cruise | null>(null);
  const [pendingFormData, setPendingFormData] = useState<CruiseFormData | null>(null);
  
  // Date picker state
  const [departureDate, setDepartureDate] = useState<Date | null>(null);
  const [returnDate, setReturnDate] = useState<Date | null>(null);
  
  // Refs to track previous values for port date reset logic
  const prevDepartureDateRef = useRef<string | null>(null);
  const prevReturnDateRef = useRef<string | null>(null);
  const lastSetDepartureDateRef = useRef<string>('');
  const lastSetReturnDateRef = useRef<string>('');
  const prevShipNameRef = useRef<string>(''); // Track previous ship name to detect changes
  
  // Ref to track if component is still mounted (prevents state updates after unmount/logout)
  const isMountedRef = useRef(true);

  // Helper function to safely parse dates
  const safeParseDate = (dateString: string | null | undefined): Date | undefined => {
    if (!dateString) return undefined;
    try {
      const parsed = parseISO(dateString);
      return isValid(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  };

  const { register, control, handleSubmit, formState: { errors }, watch, setValue } = useForm<CruiseFormData>({
    defaultValues: {
      ports: [],
      travelDetails: [],
      isGuaranteedCabin: false,
      isComplimentaryCasinoSailing: false,
      hasDrinkPackage: false,
      hasInternetPackage: false
    }
  });

  const { fields: portFields, append: appendPort, remove: removePort, replace: replacePorts } = useFieldArray({
    control,
    name: "ports"
  });

  const { fields: travelFields, append: appendTravel, remove: removeTravel } = useFieldArray({
    control,
    name: "travelDetails"
  });

  const watchDepartureDate = watch('departureDate');
  const watchReturnDate = watch('returnDate');
  const watchCruiseLine = watch('cruiseLine');
  const watchShipName = watch('shipName');
  const watchPorts = watch('ports');
  const isGuaranteedCabin = watch('isGuaranteedCabin');
  const isComplimentaryCasinoSailing = watch('isComplimentaryCasinoSailing');

  // Watch for changes in departure and return dates to reset ports
  // Skip clearing if we're loading from itinerary (loadingItinerary flag)
  useEffect(() => {
    if (loadingItinerary) return; // Don't clear ports while loading itinerary
    
    const currentDepartureDate = watchDepartureDate || null;
    const currentReturnDate = watchReturnDate || null;
    
    // Check if dates have changed and we have previous values (not initial load)
    const departureDateChanged = currentDepartureDate !== prevDepartureDateRef.current;
    const returnDateChanged = currentReturnDate !== prevReturnDateRef.current;
    
    if ((departureDateChanged || returnDateChanged) &&
        (prevDepartureDateRef.current !== null || prevReturnDateRef.current !== null)) {
      
      // Check if any ports actually have dates before clearing
      const portsWithDates = watchPorts?.filter((port: Port) => port.date && port.date.trim() !== '') || [];
      
      // Only clear dates and show toast if ports actually have dates
      if (portsWithDates.length > 0) {
        // Clear only the date field for each port, keeping other port information
        const currentPortCount = portFields.length;
        for (let i = 0; i < currentPortCount; i++) {
          setValue(`ports.${i}.date`, '');
        }
        toast.error('Port dates have been cleared due to changes in cruise departure or return dates. Please update the dates for your ports.', {
          duration: 4000,
          icon: <Calendar className="h-10 w-10" />
        });
      }
    }

    // Update refs with current values
    prevDepartureDateRef.current = currentDepartureDate;
    prevReturnDateRef.current = currentReturnDate;
  }, [watchDepartureDate, watchReturnDate, setValue, loadingItinerary, portFields.length]); // watchPorts read inside effect but not in deps to prevent re-runs on port changes

  // Update form values when date picker dates change
  useEffect(() => {
    if (departureDate) {
      const formattedDate = format(departureDate, 'yyyy-MM-dd');
      // Only update if the value is different from what we last set to avoid loops
      if (lastSetDepartureDateRef.current !== formattedDate) {
        setValue('departureDate', formattedDate, { shouldValidate: true, shouldDirty: true });
        lastSetDepartureDateRef.current = formattedDate;
      }
    } else if (lastSetDepartureDateRef.current !== '') {
      // Clear the form value if date is cleared
      setValue('departureDate', '');
      lastSetDepartureDateRef.current = '';
    }
  }, [departureDate, setValue]);

  useEffect(() => {
    if (returnDate) {
      const formattedDate = format(returnDate, 'yyyy-MM-dd');
      // Only update if the value is different from what we last set to avoid loops
      if (lastSetReturnDateRef.current !== formattedDate) {
        setValue('returnDate', formattedDate, { shouldValidate: true, shouldDirty: true });
        lastSetReturnDateRef.current = formattedDate;
      }
    } else if (lastSetReturnDateRef.current !== '') {
      // Clear the form value if date is cleared
      setValue('returnDate', '');
      lastSetReturnDateRef.current = '';
    }
  }, [returnDate, setValue]);

  // Check if cruise departure date is in the past
  const isPastCruise = departureDate ? 
    departureDate < new Date(new Date().setHours(0, 0, 0, 0)) : 
    false;

  useEffect(() => {
    if (watchCruiseLine) {
      const ships = getShipsForCruiseLine(watchCruiseLine);
      setAvailableShips(ships);
      setValue('shipName', '');
      setAvailableDepartureDates([]);
      setItineraryDetails({});
      setUseItineraryMode(false);
    }
  }, [watchCruiseLine, getShipsForCruiseLine, setValue]);

  // Clear itinerary data when ship changes
  useEffect(() => {
    // Only clear if ship name changes (not on initial load)
    if (watchShipName && prevShipNameRef.current && prevShipNameRef.current !== watchShipName) {
      // Ship has changed, clear all itinerary-related data
      setLoadingItinerary(false); // Stop any ongoing itinerary loading
      
      // Clear date state and form values
      setDepartureDate(null);
      setReturnDate(null);
      setValue('departureDate', '');
      setValue('returnDate', '');
      setValue('departurePort', '');
      
      // Clear ports
      replacePorts([]);
      setShowPortsForm(false);
      
      // Clear itinerary mode and departure dates
      setAvailableDepartureDates([]);
      setItineraryDetails({});
      setUseItineraryMode(false);
      setItinerarySelected(false); // Reset itinerary selected flag
      
      // Reset refs
      prevDepartureDateRef.current = null;
      prevReturnDateRef.current = null;
      lastSetDepartureDateRef.current = '';
      lastSetReturnDateRef.current = '';
    }
    
    // Update ref
    prevShipNameRef.current = watchShipName || '';
  }, [watchShipName, setValue, replacePorts]);

  // Fetch departure dates and itinerary details when ship is selected
  useEffect(() => {
    // Reset mounted flag when effect runs
    isMountedRef.current = true;
    
    const fetchDepartureDates = async () => {
      const cruiseLine = watchCruiseLine;
      const shipName = watchShipName;
      
      if (!cruiseLine || !shipName || !isItineraryFeatureAvailable()) {
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setAvailableDepartureDates([]);
          setItineraryDetails({});
          setUseItineraryMode(false);
        }
        return;
      }
      
      // Check if user is authenticated before making Firestore calls
      if (!currentUser) {
        debug.log('[AddCruise] Skipping itinerary fetch - user not authenticated');
        if (isMountedRef.current) {
          setAvailableDepartureDates([]);
          setItineraryDetails({});
          setUseItineraryMode(false);
          setLoadingDepartureDates(false);
        }
        return;
      }
      
      if (isMountedRef.current) {
        setLoadingDepartureDates(true);
      }
      
      try {
        const summary = await getItinerarySummaryForShip(cruiseLine, shipName);
        
        // Check if component is still mounted and user is still authenticated before updating state
        if (!isMountedRef.current || !currentUser) {
          debug.log('[AddCruise] Skipping state update - component unmounted or user logged out');
          return;
        }
        
        if (summary && summary.departureDates && summary.departureDates.length > 0) {
          // Filter out itineraries that departed more than 15 days ago
          // Keys may be composite (sailingDate_returnDate) or legacy plain sailingDate
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          const validKeys = summary.departureDates.filter((key) => {
            try {
              const sailingDate = key.includes('_') ? key.split('_')[0] : key;
              const departureDate = parseISO(sailingDate);
              if (!isValid(departureDate)) {
                return false;
              }
              const departureDateStart = new Date(departureDate);
              departureDateStart.setHours(0, 0, 0, 0);
              const daysDiff = differenceInDays(today, departureDateStart);
              return daysDiff <= ITINERARY_FILTER_DAYS;
            } catch (error) {
              debug.warn(`Error parsing key ${key}:`, error);
              return false;
            }
          });
          
          const filteredDetails: { [key: string]: { cruiseLength: number; itineraryTitle: string } } = {};
          if (summary.itineraryDetails) {
            validKeys.forEach((key) => {
              if (summary.itineraryDetails && summary.itineraryDetails[key]) {
                filteredDetails[key] = summary.itineraryDetails[key];
              }
            });
          }
          
          // Double-check mounted state before setting state
          if (isMountedRef.current && currentUser) {
            if (validKeys.length > 0) {
              setAvailableDepartureDates(validKeys);
              setItineraryDetails(filteredDetails);
              setUseItineraryMode(true);
            } else {
              setAvailableDepartureDates([]);
              setItineraryDetails({});
              setUseItineraryMode(false);
            }
          }
        } else {
          if (isMountedRef.current && currentUser) {
            setAvailableDepartureDates([]);
            setItineraryDetails({});
            setUseItineraryMode(false);
          }
        }
      } catch (error: any) {
        // Only log errors if component is still mounted and user is authenticated
        // Suppress permission errors that occur after logout
        if (isMountedRef.current && currentUser) {
          // Check if it's a permission error (likely from logout)
          if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
            debug.log('[AddCruise] Permission error - user may have logged out during fetch');
          } else {
            console.error('Error fetching departure dates:', error);
          }
          setAvailableDepartureDates([]);
          setItineraryDetails({});
          setUseItineraryMode(false);
        }
      } finally {
        // Only update loading state if component is still mounted
        if (isMountedRef.current) {
          setLoadingDepartureDates(false);
        }
      }
    };
    
    fetchDepartureDates();
    
    // Cleanup function: mark component as unmounted
    return () => {
      isMountedRef.current = false;
    };
  }, [watchCruiseLine, watchShipName, getItinerarySummaryForShip, currentUser]);

  const handleDepartureDateChange = (date: Date) => {
    setDepartureDate(date);
    // Reset return date when departure date changes
    if (returnDate) {
      setReturnDate(null);
      setValue('returnDate', '');
    }
  };

  // Handle itinerary selection (key is composite sailingDate_returnDate or legacy plain sailingDate)
  const handleItineraryDateSelect = async (key: string) => {
    const cruiseLine = watchCruiseLine;
    const shipName = watch('shipName');
    
    if (!cruiseLine || !shipName) return;
    
    if (!currentUser) {
      debug.log('[AddCruise] Skipping itinerary fetch - user not authenticated');
      toast.error('You must be logged in to load itinerary data');
      return;
    }
    
    setLoadingItinerary(true);
    try {
      const itinerary = await getItineraryByCompositeKey(cruiseLine, shipName, key);
      
      // Check if component is still mounted and user is still authenticated before updating state
      if (!isMountedRef.current || !currentUser) {
        debug.log('[AddCruise] Skipping state update - component unmounted or user logged out');
        return;
      }
      
      if (itinerary) {
        // Set departure date
        const departureDateObj = parseISO(itinerary.sailingDate);
        setDepartureDate(departureDateObj);
        setValue('departureDate', itinerary.sailingDate, { shouldValidate: true });
        setSelectedItineraryDate(key);
        
        // Set return date
        const returnDateObj = parseISO(itinerary.returnDate);
        setReturnDate(returnDateObj);
        setValue('returnDate', itinerary.returnDate, { shouldValidate: true });
        
        // Set departure port
        setValue('departurePort', itinerary.departurePort, { shouldValidate: true });
        
        // Compute and store the composite itinerary key for sync
        const itineraryKey = `${cruiseLine}_${shipName}_${itinerary.sailingDate}_${itinerary.returnDate}`;
        setSelectedItineraryKey(itineraryKey);
        setSelectedItineraryTitle(itinerary.itineraryTitle || null);
        
        // Mark that an itinerary has been selected
        setItinerarySelected(true);
        
        // Update refs BEFORE clearing/adding ports to prevent port clearing useEffect from firing
        prevDepartureDateRef.current = itinerary.sailingDate;
        prevReturnDateRef.current = itinerary.returnDate;
        
        // Set ports
        if (itinerary.ports && itinerary.ports.length > 0) {
          debug.log('[AddCruise] Loading ports from itinerary:', itinerary.ports);
          const portsWithIds = itinerary.ports.map((port) => ({
            id: uuidv4(),
            name: port.name,
            date: port.date,
            arrivalTime: port.arrivalTime || '',
            departureTime: port.departureTime || '',
            excursion: '',
            fromItinerary: true // Mark ports as loaded from itinerary
          }));
          
          debug.log('[AddCruise] Ports with IDs:', portsWithIds);
          
          // Replace all ports at once instead of clearing and appending individually
          // This prevents multiple re-renders and potential freezes
          replacePorts(portsWithIds);
          
          debug.log('[AddCruise] Ports added to form');
          setShowPortsForm(true);
        } else {
          debug.log('[AddCruise] No ports found in itinerary:', itinerary);
          // Replace with empty array instead of clearing individually
          replacePorts([]);
          setShowPortsForm(false);
        }
        
        toast.success('Itinerary data loaded successfully');
      }
    } catch (error: any) {
      // Only handle errors if component is still mounted and user is authenticated
      if (isMountedRef.current && currentUser) {
        // Check if it's a permission error (likely from logout)
        if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
          debug.log('[AddCruise] Permission error - user may have logged out during fetch');
          toast.error('Session expired. Please log in again.');
        } else {
          console.error('Error loading itinerary:', error);
          toast.error('Failed to load itinerary data');
        }
      }
    } finally {
      // Only update loading state if component is still mounted
      if (isMountedRef.current) {
        setLoadingItinerary(false);
      }
    }
  };

  const handleClearItinerarySelection = () => {
    setItinerarySelected(false);
    setLoadingItinerary(false);
    setSelectedItineraryDate('');
    setSelectedItineraryKey(null); // Clear the sync key
    setSelectedItineraryTitle(null);

    // Reset form fields populated by itinerary
    setDepartureDate(null);
    setReturnDate(null);
    setValue('departureDate', '');
    setValue('returnDate', '');
    setValue('departurePort', '');
    replacePorts([]);
    setShowPortsForm(false);

    // Reset previous refs so port date clearing effect does not trigger unexpectedly
    prevDepartureDateRef.current = null;
    prevReturnDateRef.current = null;

    toast.success('Itinerary cleared. You can select another sailing date or continue manually.');
  };

  const handleReturnDateChange = (date: Date) => {
    setReturnDate(date);
  };

  // Calculate highlighted range for return date picker
  const getHighlightedRange = (): [Date, Date] | null => {
    if (departureDate && returnDate) {
      return [departureDate, returnDate];
    }
    return null;
  };

  const renderCruiseLineOptions = () => {
    const majorLines = cruiseLines.filter(cl => cl.majorline);
    const specialtyLines = cruiseLines.filter(cl => !cl.majorline);

    return (
      <>
        {majorLines.length > 0 && (
          <optgroup label="Major Cruise Lines">
            {majorLines.map((cl) => (
              <option key={cl.name} value={cl.name}>{cl.name}</option>
            ))}
          </optgroup>
        )}
        {specialtyLines.length > 0 && (
          <optgroup label="Specialty Cruise Lines">
            {specialtyLines.map((cl) => (
              <option key={cl.name} value={cl.name}>{cl.name}</option>
            ))}
          </optgroup>
        )}
      </>
    );
  };

  const onSubmit = async (data: CruiseFormData) => {
    const submissionData = {
      ...data,
      cabinNumber: data.isGuaranteedCabin ? null : data.cabinNumber
    };

    // Check for date conflicts before proceeding
    const conflictingCruise = checkCruiseConflict({
      departureDate: submissionData.departureDate,
      returnDate: submissionData.returnDate,
      existingCruises: cruises
    });

    if (conflictingCruise) {
      // Show conflict modal
      setConflictingCruise(conflictingCruise);
      setPendingFormData(submissionData);
      setShowConflictModal(true);
      return;
    }

    // No conflict, proceed with normal flow
    await proceedWithSave(submissionData);
  };

  const proceedWithSave = async (data: CruiseFormData) => {
    // Check if this is a past cruise
    if (isPastCruise) {
      // Store the data and show completion confirmation modal
      setPendingCruiseData(data);
      setShowCompletionModal(true);
    } else {
      // For future cruises, save directly
      await saveCruise(data, false);
    }
  };

  const saveCruise = async (data: CruiseFormData, completed: boolean) => {
    try {
      setIsLoading(true);
      const cruiseData: any = {
        ...data,
        completed
      };
      // If user selected an official itinerary, attach the sync key and timestamp
      if (selectedItineraryKey) {
        cruiseData.itineraryKey = selectedItineraryKey;
        cruiseData.itinerarySyncedAt = Date.now();
        if (selectedItineraryTitle) {
          cruiseData.itineraryTitle = selectedItineraryTitle;
        }
      }
      const cruiseId = await addCruise(cruiseData);
      setIsLoading(false);
      navigate(`/cruise/${cruiseId}`, { replace: true });
    } catch (error) {
      console.error('Error adding cruise:', error);
      setIsLoading(false);
    }
  };

  const handleConflictConfirm = () => {
    // User chose to continue despite conflict
    setShowConflictModal(false);
    if (pendingFormData) {
      proceedWithSave(pendingFormData);
    }
    setConflictingCruise(null);
    setPendingFormData(null);
  };

  const handleConflictCancel = () => {
    // User chose to cancel and review dates
    setShowConflictModal(false);
    setConflictingCruise(null);
    setPendingFormData(null);
  };

  const handleCompletionConfirm = async (completed: boolean) => {
    if (!pendingCruiseData) return;
    
    setShowCompletionModal(false);
    await saveCruise(pendingCruiseData, completed);
    setPendingCruiseData(null);
  };

  const handleCompletionCancel = () => {
    setShowCompletionModal(false);
    setPendingCruiseData(null);
  };

  const handleAddPort = () => {
    appendPort({ id: uuidv4(), name: '', date: '', arrivalTime: '', departureTime: '', excursion: '' });
    setShowPortsForm(true);
  };

  const handleAddTravel = (type: 'rental-car' | 'train' | 'airplane') => {
    appendTravel({ id: uuidv4(), type });
    setShowTravelForm(true);
  };

  const renderTravelFields = (field: TravelDetail, index: number) => {
    switch (field.type) {
      case 'airplane':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Airline</label>
              <input
                {...register(`travelDetails.${index}.company`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Delta Airlines"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Flight Number</label>
              <input
                {...register(`travelDetails.${index}.flightNumber`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="DL1234"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Departure Airport</label>
              <input
                {...register(`travelDetails.${index}.departureLocation`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="JFK"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Airport</label>
              <input
                {...register(`travelDetails.${index}.arrivalLocation`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="MIA"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Departure Time</label>
              <input
                type="datetime-local"
                {...register(`travelDetails.${index}.departureTime`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Time</label>
              <input
                type="datetime-local"
                {...register(`travelDetails.${index}.arrivalTime`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reservation Number</label>
              <input
                {...register(`travelDetails.${index}.reservationNumber`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="ABCD123"
              />
            </div>
          </div>
        );

      case 'train':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Train Company</label>
              <input
                {...register(`travelDetails.${index}.company`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Amtrak"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Train Number</label>
              <input
                {...register(`travelDetails.${index}.trainNumber`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="123"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Departure Station</label>
              <input
                {...register(`travelDetails.${index}.departureLocation`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Penn Station"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Station</label>
              <input
                {...register(`travelDetails.${index}.arrivalLocation`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Union Station"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Departure Time</label>
              <input
                type="datetime-local"
                {...register(`travelDetails.${index}.departureTime`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Time</label>
              <input
                type="datetime-local"
                {...register(`travelDetails.${index}.arrivalTime`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reservation Number</label>
              <input
                {...register(`travelDetails.${index}.reservationNumber`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="ABCD123"
              />
            </div>
          </div>
        );

      case 'rental-car':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rental Company</label>
              <input
                {...register(`travelDetails.${index}.company`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Enterprise"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Car Type</label>
              <input
                {...register(`travelDetails.${index}.carType`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="SUV, Sedan, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Location</label>
              <input
                {...register(`travelDetails.${index}.pickupLocation`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Airport Location"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Return Location</label>
              <input
                {...register(`travelDetails.${index}.returnLocation`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Port Location"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Time</label>
              <input
                type="datetime-local"
                {...register(`travelDetails.${index}.pickupTime`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              />
            </div>
            <div className="min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Return Time</label>
              <input
                type="datetime-local"
                {...register(`travelDetails.${index}.returnTime`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reservation Number</label>
              <input
                {...register(`travelDetails.${index}.reservationNumber`)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="ABCD123"
              />
            </div>
          </div>
        );
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <motion.div 
      className="max-w-3xl mx-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* Date Conflict Modal */}
      {showConflictModal && conflictingCruise && pendingFormData && (
        <CruiseConflictModal
          isOpen={showConflictModal}
          onClose={handleConflictCancel}
          onConfirm={handleConflictConfirm}
          conflictingCruise={conflictingCruise}
          newCruiseDates={{
            departureDate: pendingFormData.departureDate,
            returnDate: pendingFormData.returnDate,
            shipName: pendingFormData.shipName,
            cruiseLine: pendingFormData.cruiseLine
          }}
        />
      )}

      {/* Completion Confirmation Modal - Using Portal */}
      {showCompletionModal && pendingCruiseData && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center">
                <AlertTriangle className="h-6 w-6 text-blue-500 mr-3" />
                <h2 className="text-xl font-semibold text-gray-800">Cruise Completion</h2>
              </div>
              <button
                onClick={handleCompletionCancel}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
                disabled={isLoading}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
            <p className="text-gray-600">
              The cruise you're adding has a departure date in the past. Have you already completed this cruise?
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-sm">
                <p className="font-medium text-blue-900">{pendingCruiseData.shipName}</p>
                <p className="text-blue-700">{pendingCruiseData.cruiseLine}</p>
                <p className="text-blue-600">
                  {new Date(pendingCruiseData.departureDate).toLocaleDateString()} - {new Date(pendingCruiseData.returnDate).toLocaleDateString()}
                </p>
              </div>
            </div>
            
            <div className="space-y-3 pt-4">
              <button
                onClick={() => handleCompletionConfirm(true)}
                disabled={isLoading}
                className="w-full flex items-center justify-center px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Dice className="h-5 w-5 mr-2" />
                    Yes, Mark as Completed
                  </>
                )}
              </button>
              
              <button
                onClick={() => handleCompletionConfirm(false)}
                disabled={isLoading}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium"
              >
                No, Save as Planned
              </button>
              
              <button
                onClick={handleCompletionCancel}
                disabled={isLoading}
                className="w-full px-4 py-2 text-gray-500 hover:text-gray-700 transition duration-200"
              >
                Go Back to Edit
              </button>
            </div>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      <motion.div 
        className="mb-6 flex items-center"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <button 
          onClick={() => navigate(-1)}
          className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
          aria-label="Go back"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Add New Cruise</h1>
          <p className="text-gray-600 mt-1">Enter the details of your cruise vacation</p>
        </div>
      </motion.div>

      <motion.div 
        className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <div className="flex">
          <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-3" />
          <p className="text-sm text-blue-700">
            Fields marked with an asterisk (*) are required
          </p>
        </div>
      </motion.div>

      <motion.form 
        onSubmit={handleSubmit(onSubmit)} 
        className="bg-white rounded-lg shadow-md p-6"
        variants={container}
        initial="hidden"
        animate="show"
      >
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          variants={item}
        >
          <div>
            <label htmlFor="cruiseLine" className="block text-sm font-medium text-gray-700 mb-1">
              Cruise Line *
            </label>
            <select
              id="cruiseLine"
              {...register('cruiseLine', { required: 'Cruise line is required' })}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-900 ${
                errors.cruiseLine ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-secondary-500 focus:border-transparent'
              }`}
            >
              <option value="">Select cruise line</option>
              {renderCruiseLineOptions()}
            </select>
            {errors.cruiseLine && (
              <p className="mt-1 text-sm text-red-600">{errors.cruiseLine.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="shipName" className="block text-sm font-medium text-gray-700 mb-1">
              Ship Name *
            </label>
            <select
              id="shipName"
              {...register('shipName', { required: 'Ship name is required' })}
              disabled={!watchCruiseLine}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-900 ${
                errors.shipName ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-secondary-500 focus:border-transparent'
              } ${!watchCruiseLine ? 'bg-gray-100' : ''}`}
            >
              <option value="">
                {watchCruiseLine ? 'Select ship' : 'Select cruise line first'}
              </option>
              {availableShips.active.length > 0 && (
                <optgroup label="Active Fleet">
                  {availableShips.active.map((ship) => (
                    <option key={ship} value={ship}>{ship}</option>
                  ))}
                </optgroup>
              )}
              {availableShips.retired.length > 0 && (
                <optgroup label="Retired Fleet">
                  {availableShips.retired.map((ship) => (
                    <option key={ship} value={ship}>{ship} (Retired)</option>
                  ))}
                </optgroup>
              )}
            </select>
            {errors.shipName && (
              <p className="mt-1 text-sm text-red-600">{errors.shipName.message}</p>
            )}
          </div>

          {/* Itinerary Selection - Show above date fields when available */}
          {useItineraryMode && availableDepartureDates.length > 0 && (
            <div className="md:col-span-2">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Calendar className="h-5 w-5 text-blue-600 mr-2" />
                  <h3 className="text-lg font-semibold text-blue-900">
                    Available Itineraries
                  </h3>
                </div>
                <p className="text-sm text-blue-700 mb-3">
                  We have itinerary data on file for this ship. Select a sailing date to auto-fill your cruise details, or continue entering information manually below.
                </p>
                {loadingDepartureDates ? (
                  <div className="flex items-center text-sm text-blue-600">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Loading available dates...
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-blue-900 mb-2">
                      Select Sailing Date:
                    </label>
                    <select
                      value={selectedItineraryDate}
                      onChange={(e) => {
                        if (e.target.value) {
                          handleItineraryDateSelect(e.target.value);
                        } else {
                          setSelectedItineraryDate('');
                        }
                      }}
                      disabled={loadingItinerary}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent bg-white text-gray-900"
                    >
                      <option value="">Select sailing date...</option>
                      {availableDepartureDates.map((key) => {
                        const details = itineraryDetails[key];
                        const sailingDate = key.includes('_') ? key.split('_')[0] : key;
                        const dateFormatted = format(parseISO(sailingDate), 'MMM d, yyyy');
                        let displayText = dateFormatted;
                        
                        if (details?.itineraryTitle) {
                          displayText = `${dateFormatted} - ${details.itineraryTitle}`;
                        } else if (details?.cruiseLength) {
                          displayText = `${dateFormatted} - ${details.cruiseLength} day cruise`;
                        }
                        
                        return (
                          <option key={key} value={key}>
                            {displayText}
                          </option>
                        );
                      })}
                    </select>
                    {loadingItinerary && (
                      <div className="flex items-center text-sm text-blue-600 mt-2">
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading itinerary...
                      </div>
                    )}
                    {itinerarySelected && (
                      <div className="mt-3 flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={handleClearItinerarySelection}
                          className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-white border border-blue-300 rounded-md hover:bg-blue-100 transition-colors"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Clear itinerary selection
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div>
            <CruiseDatePicker
              label="Departure Date"
              value={departureDate}
              onChange={handleDepartureDateChange}
              highlightedRange={departureDate && returnDate ? [departureDate, returnDate] : null}
              highlightedDate={returnDate || undefined}
              error={errors.departureDate?.message}
              required
              disabled={itinerarySelected}
            />
            <input
              type="hidden"
              {...register('departureDate', { required: 'Departure date is required' })}
            />
          </div>

          <div>
            <CruiseDatePicker
              label="Return Date"
              value={returnDate}
              onChange={handleReturnDateChange}
              minDate={departureDate || undefined}
              activeStartDate={departureDate ? startOfMonth(departureDate) : undefined}
              highlightedRange={getHighlightedRange()}
              highlightedDate={departureDate || undefined}
              placeholder="Select return date"
              disabled={!departureDate || itinerarySelected}
              error={errors.returnDate?.message}
              required
            />
            <input
              type="hidden"
              {...register('returnDate', { 
                required: 'Return date is required',
                validate: value => !departureDate || new Date(value) >= departureDate || 'Return date must be on or after departure date'
              })}
            />
          </div>

          <div>
            <label htmlFor="departurePort" className="block text-sm font-medium text-gray-700 mb-1">
              Departure Port *
            </label>
            <input
              id="departurePort"
              {...register('departurePort', { required: 'Departure port is required' })}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-900 ${
                errors.departurePort ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-secondary-500 focus:border-transparent'
              } ${itinerarySelected ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              placeholder="Miami,FL"
              disabled={itinerarySelected}
            />
            {errors.departurePort && (
              <p className="mt-1 text-sm text-red-600">{errors.departurePort.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="cabinType" className="block text-sm font-medium text-gray-700 mb-1">
              Cabin Type
            </label>
            <select
              id="cabinType"
              {...register('cabinType')}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-900 ${
                errors.cabinType ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-secondary-500 focus:border-transparent'
              }`}
            >
              <option value="">Select cabin type</option>
              <option value="Interior">Interior</option>
              <option value="Ocean View">Ocean View</option>
              <option value="Balcony">Balcony</option>
              <option value="Suite">Suite</option>
            </select>
            {errors.cabinType && (
              <p className="mt-1 text-sm text-red-600">{errors.cabinType.message}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="isGuaranteedCabin"
                {...register('isGuaranteedCabin')}
                className="h-4 w-4 text-secondary-600 focus:ring-secondary-500 border-gray-300 rounded"
              />
              <label htmlFor="isGuaranteedCabin" className="ml-2 block text-sm text-gray-700">
                This is a guaranteed cabin (cabin number will be assigned later)
              </label>
            </div>

            {!isGuaranteedCabin && (
              <div className="mb-6">
                <label htmlFor="cabinNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Cabin Number
                </label>
                <input
                  id="cabinNumber"
                  {...register('cabinNumber')}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-gray-900 ${
                    errors.cabinNumber ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-secondary-500 focus:border-transparent'
                  }`}
                  placeholder="8245"
                />
                {errors.cabinNumber && (
                  <p className="mt-1 text-sm text-red-600">{errors.cabinNumber.message}</p>
                )}
              </div>
            )}

            <div className="mb-6">
              <label htmlFor="bookingNumber" className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center">
                  <FileText className="h-4 w-4 mr-1" />
                  Booking Number
                </div>
              </label>
              <input
                id="bookingNumber"
                {...register('bookingNumber')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="ABC123456"
              />
              <p className="mt-1 text-sm text-gray-500">
                Your cruise line booking confirmation number
              </p>
            </div>

            <div className="mb-6">
              <label htmlFor="diningTime" className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center">
                  <ChefHat className="h-4 w-4 mr-1" />
                  Dining Time
                </div>
              </label>
              <select
                id="diningTime"
                {...register('diningTime')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
              >
                <option value="">Select dining time</option>
                <option value="Early">Early</option>
                <option value="Late">Late</option>
                <option value="Flexible">Flexible</option>
                <option value="Other">Other</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Your preferred dining time for the main dining room
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Packages
              </label>
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasDrinkPackage"
                    {...register('hasDrinkPackage')}
                    className="h-4 w-4 text-secondary-600 focus:ring-secondary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasDrinkPackage" className="ml-2 flex items-center text-sm text-gray-700">
                    <GlassWater className="h-4 w-4 mr-2 text-blue-500" />
                    Drink Package
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasInternetPackage"
                    {...register('hasInternetPackage')}
                    className="h-4 w-4 text-secondary-600 focus:ring-secondary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasInternetPackage" className="ml-2 flex items-center text-sm text-gray-700">
                    <Wifi className="h-4 w-4 mr-2 text-purple-500" />
                    Internet Package
                  </label>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Select if you have purchased these packages for this cruise
              </p>
            </div>

            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="isComplimentaryCasinoSailing"
                  {...register('isComplimentaryCasinoSailing')}
                  className="h-4 w-4 text-secondary-600 focus:ring-secondary-500 border-gray-300 rounded"
                />
                <label htmlFor="isComplimentaryCasinoSailing" className="ml-2 block text-sm text-gray-700">
                  This cruise is a complimentary casino sailing
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="cruiseCost" className="block text-sm font-medium text-gray-700 mb-1">
                    {isComplimentaryCasinoSailing ? 'Complimentary Cruise Value' : 'Cruise Cost'}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CurrencyIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      id="cruiseCost"
                      {...register('cruiseCost')}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="portTaxesFees" className="block text-sm font-medium text-gray-700 mb-1">
                    Port Taxes and Fees
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CurrencyIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      id="portTaxesFees"
                      {...register('portTaxesFees')}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="additionalCosts" className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Cruise Costs (Upgrades, Deposits, Drink Package, etc)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CurrencyIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      step="0.01"
                      id="additionalCosts"
                      {...register('additionalCosts')}
                      className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              {/* Only show post-cruise fields if departure date is in the past */}
              {isPastCruise && (
                <>
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Post-Cruise Details</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Since this cruise is in the past, you can add details about your actual expenses.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label htmlFor="onboardExpenses" className="block text-sm font-medium text-gray-700 mb-1">
                        Onboard Expenses
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <CurrencyIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          id="onboardExpenses"
                          {...register('onboardExpenses')}
                          className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                          placeholder="0.00"
                        />
                      </div>
                      <p className="mt-1 text-sm text-gray-500">
                        Spa, specialty dining, drinks, shopping, etc.
                      </p>
                    </div>

                    <div>
                      <label htmlFor="gamblingWins" className="block text-sm font-medium text-gray-700 mb-1">
                        Gambling Wins
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <CurrencyIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          id="gamblingWins"
                          {...register('gamblingWins')}
                          className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="gamblingLosses" className="block text-sm font-medium text-gray-700 mb-1">
                        Gambling Losses
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <CurrencyIcon className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          id="gamblingLosses"
                          {...register('gamblingLosses')}
                          className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </motion.div>

        {/* Travel Agent Information */}
        <div className="border-t border-gray-200 pt-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Travel Agent Information</h3>
          <p className="text-sm text-gray-600 mb-4">
            Optional: Add your travel agent's contact information for easy reference.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="travelAgentName" className="block text-sm font-medium text-gray-700 mb-1">
                Agent Name
              </label>
              <input
                type="text"
                id="travelAgentName"
                {...register('travelAgentName')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="Agent's full name"
              />
            </div>

            <div>
              <label htmlFor="travelAgentPhone" className="block text-sm font-medium text-gray-700 mb-1">
                Agent Phone
              </label>
              <input
                type="tel"
                id="travelAgentPhone"
                {...register('travelAgentPhone')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="(555) 123-4567"
              />
            </div>

            <div>
              <label htmlFor="travelAgentEmail" className="block text-sm font-medium text-gray-700 mb-1">
                Agent Email
              </label>
              <input
                type="email"
                id="travelAgentEmail"
                {...register('travelAgentEmail')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                placeholder="agent@travelagency.com"
              />
            </div>
          </div>
        </div>

        <motion.div 
          className="border-t border-gray-200 pt-6 mt-6"
          variants={item}
        >
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            id="notes"
            {...register('notes')}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
            placeholder="Any additional information about your cruise..."
          />
        </motion.div>

        <motion.div 
          className="mt-8"
          variants={item}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Travel Details</h3>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleAddTravel('airplane')}
                className="flex items-center px-3 py-1.5 text-sm bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
              >
                <Plane className="h-4 w-4 mr-1" />
                Add Flight
              </button>
              <button
                type="button"
                onClick={() => handleAddTravel('train')}
                className="flex items-center px-3 py-1.5 text-sm bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
              >
                <Train className="h-4 w-4 mr-1" />
                Add Train
              </button>
              <button
                type="button"
                onClick={() => handleAddTravel('rental-car')}
                className="flex items-center px-3 py-1.5 text-sm bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
              >
                <Car className="h-4 w-4 mr-1" />
                Add Car
              </button>
            </div>
          </div>

          {showTravelForm && travelFields.length > 0 && (
            <div className="space-y-6">
              {travelFields.map((field, index) => (
                <div
                  key={field.id}
                  className="p-4 border border-gray-200 rounded-md relative"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      {field.type === 'airplane' && <Plane className="h-5 w-5 text-blue-500 mr-2" />}
                      {field.type === 'train' && <Train className="h-5 w-5 text-purple-500 mr-2" />}
                      {field.type === 'rental-car' && <Car className="h-5 w-5 text-green-500 mr-2" />}
                      <h4 className="font-medium text-gray-900">
                        {field.type === 'airplane' && 'Flight Details'}
                        {field.type === 'train' && 'Train Details'}
                        {field.type === 'rental-car' && 'Rental Car Details'}
                      </h4>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        removeTravel(index);
                        if (travelFields.length === 1) setShowTravelForm(false);
                      }}
                      className="text-gray-500 hover:text-red-500"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  {renderTravelFields(field, index)}
                </div>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div 
          className="mt-8"
          variants={item}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Ports of Call</h3>
            <button
              type="button"
              onClick={handleAddPort}
              className="flex items-center px-3 py-1.5 text-sm bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
            >
              <PlusCircle className="h-4 w-4 mr-1" />
              Add Port
            </button>
          </div>

          {showPortsForm && portFields.length > 0 && (
            <div className="space-y-4">
              {portFields.map((field, index) => {
                const isFromItinerary = field.fromItinerary === true;
                return (
                <div
                  key={field.id}
                  className={`p-4 border rounded-md relative ${
                    isFromItinerary ? 'bg-gray-50 border-gray-300' : 'border-gray-200'
                  }`}
                >
                  {!isFromItinerary && (
                    <button
                      type="button"
                      onClick={() => {
                        removePort(index);
                        if (portFields.length === 1) setShowPortsForm(false);
                      }}
                      className="absolute top-2 right-2 text-gray-500 hover:text-red-500"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}
                  {isFromItinerary && (
                    <div className="absolute top-2 right-2">
                      <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
                        From Itinerary
                      </span>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Port Name
                      </label>
                      <input
                        {...register(`ports.${index}.name`)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900 ${
                          isFromItinerary 
                            ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                            : 'border-gray-300'
                        }`}
                        placeholder="Nassau, Bahamas"
                        disabled={isFromItinerary}
                      />
                    </div>

                    <div className="min-w-0">
                      <CruiseDatePicker
                        label="Date"
                        value={watch(`ports.${index}.date`) ? (safeParseDate(watch(`ports.${index}.date`)) || null) : null}
                        onChange={(date: Date) => {
                          debug.log('Port date onChange called:', { 
                            index, 
                            date, 
                            formattedDate: format(date, 'yyyy-MM-dd'),
                            currentFieldValue: field.date,
                            watchedValue: watch(`ports.${index}.date`)
                          });
                          setValue(`ports.${index}.date`, format(date, 'yyyy-MM-dd'));
                          debug.log('After setValue - watched value:', watch(`ports.${index}.date`));
                        }}
                        minDate={watchDepartureDate ? safeParseDate(watchDepartureDate) : undefined}
                        maxDate={watchReturnDate ? safeParseDate(watchReturnDate) : undefined}
                        activeStartDate={watchDepartureDate ? safeParseDate(watchDepartureDate) : undefined}
                        disabled={!watchDepartureDate || !watchReturnDate || isFromItinerary}
                        error={errors.ports?.[index]?.date?.message}
                        required={false}
                      />
                      <input
                        type="hidden"
                        {...register(`ports.${index}.date`, {
                          validate: value => {
                            if (!value || !watchDepartureDate || !watchReturnDate) return true;
                            const portDate = new Date(value);
                            const departure = new Date(watchDepartureDate);
                            const returnDate = new Date(watchReturnDate);
                            if (portDate < departure) {
                              return 'Port date cannot be before cruise departure date';
                            }
                            if (portDate > returnDate) {
                              return 'Port date cannot be after cruise return date';
                            }
                            return true;
                          }
                        })}
                      />
                      {errors.ports?.[index]?.date && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.ports[index]?.date?.message}
                        </p>
                      )}
                    </div>

                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Arrival Time
                      </label>
                      <input
                        type="time"
                        {...register(`ports.${index}.arrivalTime`)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900 ${
                          isFromItinerary 
                            ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                            : 'border-gray-300'
                        }`}
                        disabled={isFromItinerary}
                      />
                    </div>

                    <div className="min-w-0">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Departure Time
                      </label>
                      <input
                        type="time"
                        {...register(`ports.${index}.departureTime`)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900 ${
                          isFromItinerary 
                            ? 'bg-gray-100 border-gray-300 cursor-not-allowed' 
                            : 'border-gray-300'
                        }`}
                        disabled={isFromItinerary}
                      />
                    </div>

                    <div className="min-w-0 md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Excursion
                      </label>
                      <input
                        {...register(`ports.${index}.excursion`)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900"
                        placeholder="Beach day, snorkeling tour, etc."
                      />
                    </div>
                  </div>
                </div>
              )})}
            </div>
          )}
        </motion.div>

        <motion.div 
          className="mt-8 flex justify-end"
          variants={item}
        >
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md mr-4 hover:bg-gray-50 transition duration-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center px-6 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium"
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <CustomCruiseIcon size={34} className="mr-2" />
            )}
            {isLoading ? 'Saving...' : 'Save Cruise'}
          </button>
        </motion.div>
      </motion.form>
    </motion.div>
  );
};

export default AddCruise;